import { COMPONENT_NAME, EVENT_NAMES } from '../constants';

import type { Strings } from '../types';
import type { Logger } from '@web-platform/global-components-types/utils';

const CONSENT_MARKETS = ['kr', 'cn'];

const CONSENT_STRING_KEYS: Array<Partial<keyof Strings>> = [
  'koreaPrivacyConsent',
  'koreaTermsConsent',
  'chinaSensitiveDataConsent',
  'chinaShareDataConsent',
  'chinaTermsConsent',
];

export type ConsentLabel = {
  id: string;
  label: string;
  ariaLabel: string;
};

const stripLinkTags = (text: string): string =>
  text.replace(/@@\w+@@/g, '').replace(/<\/?link\d+>/g, '');

const getKoreaConsentData = (strings: Strings): ConsentLabel[] => [
  {
    id: 'koreaPrivacyConsent',
    label: stripLinkTags(strings.koreaPrivacyConsent),
    ariaLabel: stripLinkTags(strings.koreaPrivacyConsent),
  },
  {
    id: 'koreaTermsConsent',
    label: stripLinkTags(strings.koreaTermsConsent),
    ariaLabel: stripLinkTags(strings.koreaTermsConsent),
  },
];

const getChinaConsentData = (strings: Strings): ConsentLabel[] => [
  {
    id: 'chinaSensitiveDataConsent',
    label: stripLinkTags(strings.chinaSensitiveDataConsent),
    ariaLabel: stripLinkTags(strings.chinaSensitiveDataConsent),
  },
  {
    id: 'chinaShareDataConsent',
    label: stripLinkTags(strings.chinaShareDataConsent),
    ariaLabel: stripLinkTags(strings.chinaShareDataConsent),
  },
  {
    id: 'chinaTermsConsent',
    label: stripLinkTags(strings.chinaTermsConsent),
    ariaLabel: stripLinkTags(strings.chinaTermsConsent),
  },
];

const isConsentRequired = (market: string, strings: Strings, logger: Logger) => {
  let consentRequired = false;

  consentRequired = CONSENT_MARKETS.includes(market.toLowerCase());
  CONSENT_STRING_KEYS.forEach((key) => {
    if (!strings[key]) {
      consentRequired = false;
      logger.logOperationalEvent({
        component: COMPONENT_NAME,
        eventName: EVENT_NAMES.CONSENT_MODAL_MISSING_STRINGS,
      });
    }
  });

  return consentRequired;
};

export const getConsentData = (
  market: string,
  strings: Strings,
  logger: Logger,
): { labels: ConsentLabel[]; strings: string[] } | null => {
  if (!isConsentRequired(market, strings, logger)) {
    return null;
  }

  switch (market.toLowerCase()) {
    case 'kr':
      return {
        labels: getKoreaConsentData(strings),
        strings: [strings.koreaPrivacyConsent, strings.koreaTermsConsent],
      };
    case 'cn':
      return {
        labels: getChinaConsentData(strings),
        strings: [
          strings.chinaTermsConsent,
          strings.chinaShareDataConsent,
          strings.chinaSensitiveDataConsent,
        ],
      };
    default:
      logger.logOperationalEvent({
        component: COMPONENT_NAME,
        eventName: EVENT_NAMES.CONSENT_MODAL_NO_MARKET_CONSENT_DATA,
      });
      return null;
  }
};
